import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import CarCard from '../components/CarCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  User, MapPin, Phone, Globe, Instagram, Star, Shield, Car,
  Heart, MessageCircle, Send, Calendar, ChevronRight, ExternalLink,
  Facebook, Twitter, Check, Loader2, Image as ImageIcon, Video, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../LanguageContext';

const PublicProfilePage = () => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const { user: me, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('feed');
  const [postText, setPostText] = useState('');
  const [posting, setPosting] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [submittingReview, setSubmittingReview] = useState(false);

  const isOwn = me?.id === userId;
  const backendBase = API.replace('/api', '');

  useEffect(() => {
    fetchProfile();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = () => {
    setLoading(true);
    axios.get(`${API}/profile/${userId}`)
      .then(r => setProfile(r.data))
      .catch(() => toast.error(t('profile.profileNotFound')))
      .finally(() => setLoading(false));
  };

  const handlePost = async () => {
    if (!postText.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/posts`, { content: postText, media_type: 'text' }, { headers: { Authorization: `Bearer ${token}` } });
      setPostText('');
      fetchProfile();
      toast.success(t('profile.posted'));
    } catch { toast.error(t('profile.postFailed')); }
    finally { setPosting(false); }
  };

  const handleLike = async (postId) => {
    if (!me && !token) { toast.error(t('profile.loginLike')); return; }
    try {
      await axios.post(`${API}/posts/${postId}/like`, {}, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      fetchProfile();
    } catch { }
  };

  const handleReview = async () => {
    if (!reviewText.trim()) return;
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, { target_user_id: userId, rating: reviewRating, comment: reviewText }, { headers: { Authorization: `Bearer ${token}` } });
      setReviewText('');
      fetchProfile();
      toast.success(t('profile.reviewAdded'));
    } catch (e) { toast.error(e.response?.data?.detail || t('common.error')); }
    finally { setSubmittingReview(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;
  if (!profile) return <div className="min-h-screen flex items-center justify-center text-gray-500">{t('profile.profileNotFound')}</div>;

  const trustColor = profile.trust_score >= 70 ? 'text-green-400' : profile.trust_score >= 40 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div className="min-h-screen">
      {/* Cover */}
      <div className="relative h-36 sm:h-52 bg-gradient-to-r from-cyan-900/20 via-transparent to-cyan-900/20">
        {profile.cover_url && <img src={`${backendBase}${profile.cover_url}`} alt="" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#030810] to-transparent" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-16 relative z-10">
        {/* Profile Header */}
        <div className="flex flex-col sm:flex-row items-start gap-4 mb-6">
          <div className="w-24 h-24 rounded-full bg-[#0a0a0f] border-4 border-[#030810] overflow-hidden shrink-0">
            {profile.avatar_url ? (
              <img src={`${backendBase}${profile.avatar_url}`} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-cyan-500/20 flex items-center justify-center"><User className="w-10 h-10 text-cyan-500" /></div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-2xl font-bold">{profile.name}</h1>
              {profile.is_verified && <Shield className="w-5 h-5 text-cyan-500" title={t('profile.verified')} />}
            </div>
            {profile.bio && <p className="text-sm text-gray-400 mt-1 max-w-lg">{profile.bio}</p>}
            <div className="flex items-center gap-4 mt-2 flex-wrap text-xs text-gray-500">
              {profile.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {profile.location}</span>}
              {profile.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {profile.phone}</span>}
              {profile.website && <a href={profile.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-cyan-500 hover:underline"><Globe className="w-3 h-3" /> {t('profile.website')}</a>}
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {t('profile.joined')} {new Date(profile.created_at).toLocaleDateString()}</span>
            </div>
            {/* Social Links */}
            {profile.social_links && (
              <div className="flex gap-2 mt-2">
                {profile.social_links.instagram && <a href={`https://instagram.com/${profile.social_links.instagram.replace('@', '')}`} target="_blank" rel="noreferrer" className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center hover:bg-cyan-500/20 transition-colors"><Instagram className="w-3.5 h-3.5 text-gray-400" /></a>}
                {profile.social_links.facebook && <a href={profile.social_links.facebook} target="_blank" rel="noreferrer" className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center hover:bg-cyan-500/20 transition-colors"><Facebook className="w-3.5 h-3.5 text-gray-400" /></a>}
                {profile.social_links.twitter && <a href={`https://twitter.com/${profile.social_links.twitter.replace('@', '')}`} target="_blank" rel="noreferrer" className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center hover:bg-cyan-500/20 transition-colors"><Twitter className="w-3.5 h-3.5 text-gray-400" /></a>}
              </div>
            )}
          </div>

          {/* Stats Cards */}
          <div className="flex gap-3 sm:gap-4 shrink-0">
            <div className="text-center">
              <p className="font-heading text-lg font-bold">{profile.cars?.length || 0}</p>
              <p className="text-[10px] text-gray-500">{t('profile.listings')}</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-lg font-bold flex items-center justify-center gap-0.5"><Star className="w-3 h-3 text-yellow-400" /> {profile.avg_rating || '-'}</p>
              <p className="text-[10px] text-gray-500">{profile.review_count} {t('profile.reviews')}</p>
            </div>
            <div className="text-center">
              <p className={`font-heading text-lg font-bold ${trustColor}`}>{profile.trust_score}</p>
              <p className="text-[10px] text-gray-500">{t('profile.trustScore')}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/5 mb-5 overflow-x-auto scrollbar-hide">
          {[
            { key: 'feed', label: t('profile.feed') },
            { key: 'cars', label: t('profile.cars') },
            { key: 'reviews', label: t('profile.reviewsTab') }
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`px-4 py-2.5 text-xs font-medium capitalize border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.key ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-500 hover:text-white'}`} data-testid={`profile-tab-${tab.key}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Feed Tab */}
        {activeTab === 'feed' && (
          <div className="max-w-2xl">
            {/* Post Creator */}
            {isOwn && (
              <div className="glass-card rounded-xl p-4 mb-4">
                <Textarea value={postText} onChange={e => setPostText(e.target.value)} placeholder={t('profile.postPlaceholder')} rows={3} className="text-sm bg-black/30 border-white/10 mb-3" data-testid="new-post-input" />
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <button className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-gray-500 hover:text-cyan-500"><ImageIcon className="w-4 h-4" /></button>
                    <button className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-gray-500 hover:text-cyan-500"><Video className="w-4 h-4" /></button>
                  </div>
                  <Button onClick={handlePost} disabled={posting || !postText.trim()} size="sm" className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-xs" data-testid="submit-post-btn">
                    {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} {t('profile.post')}
                  </Button>
                </div>
              </div>
            )}

            {/* Posts */}
            {(!profile.posts || profile.posts.length === 0) ? (
              <div className="text-center py-10 text-gray-600 text-sm">{t('profile.noPosts')}</div>
            ) : (
              <div className="space-y-3">
                {profile.posts.map(post => (
                  <PostCard key={post.id} post={post} onLike={handleLike} token={token} meId={me?.id} backendBase={backendBase} t={t} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cars Tab */}
        {activeTab === 'cars' && (
          <div>
            {(!profile.cars || profile.cars.length === 0) ? (
              <div className="text-center py-10 text-gray-600 text-sm">{t('profile.noListings')}</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {profile.cars.map(car => <CarCard key={car.id} car={car} />)}
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="max-w-2xl">
            {/* Add Review */}
            {token && !isOwn && (
              <div className="glass-card rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-gray-400">{t('profile.rating')}</span>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(s => (
                      <button key={s} onClick={() => setReviewRating(s)} className="p-0.5">
                        <Star className={`w-4 h-4 ${s <= reviewRating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <Textarea value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder={t('profile.reviewPlaceholder')} rows={2} className="text-sm bg-black/30 border-white/10 mb-3" data-testid="review-input" />
                <Button onClick={handleReview} disabled={submittingReview} size="sm" className="bg-cyan-500 text-black hover:bg-cyan-400 font-bold text-xs" data-testid="submit-review-btn">
                  {submittingReview ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3 mr-1" />} {t('profile.submitReview')}
                </Button>
              </div>
            )}
            {(!profile.reviews || profile.reviews.length === 0) ? (
              <div className="text-center py-10 text-gray-600 text-sm">{t('profile.noReviews')}</div>
            ) : (
              <div className="space-y-3">
                {profile.reviews.map(r => (
                  <div key={r.id} className="glass-card rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-full bg-cyan-500/20 flex items-center justify-center overflow-hidden">
                        {r.author_avatar ? <img src={`${backendBase}${r.author_avatar}`} alt="" className="w-full h-full object-cover" /> : <User className="w-3 h-3 text-cyan-500" />}
                      </div>
                      <span className="text-xs font-semibold">{r.author_name}</span>
                      <div className="flex gap-0.5 ml-auto">
                        {[1,2,3,4,5].map(s => <Star key={s} className={`w-3 h-3 ${s <= r.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-700'}`} />)}
                      </div>
                    </div>
                    <p className="text-sm text-gray-300">{r.comment}</p>
                    <p className="text-[10px] text-gray-600 mt-2">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* Post Card Component */
const PostCard = ({ post, onLike, token, meId, backendBase, t }) => {
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');

  const loadComments = async () => {
    setShowComments(!showComments);
    if (!showComments) {
      try {
        const r = await axios.get(`${API}/posts/${post.id}/comments`);
        setComments(r.data);
      } catch { }
    }
  };

  const submitComment = async () => {
    if (!commentText.trim() || !token) return;
    try {
      const r = await axios.post(`${API}/posts/${post.id}/comment`, { content: commentText }, { headers: { Authorization: `Bearer ${token}` } });
      setComments(prev => [...prev, r.data]);
      setCommentText('');
    } catch { toast.error(t ? t('common.error') : 'Failed'); }
  };

  const isLiked = post.likes?.includes(meId);

  return (
    <div className="glass-card rounded-xl p-4" data-testid={`post-${post.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center overflow-hidden">
          {post.user_avatar ? <img src={`${backendBase}${post.user_avatar}`} alt="" className="w-full h-full object-cover" /> : <User className="w-3.5 h-3.5 text-cyan-500" />}
        </div>
        <div>
          <p className="text-xs font-semibold">{post.user_name}</p>
          <p className="text-[10px] text-gray-600">{new Date(post.created_at).toLocaleDateString()}</p>
        </div>
      </div>
      <p className="text-sm text-gray-300 mb-3 whitespace-pre-wrap">{post.content}</p>
      {/* Media */}
      {post.media?.length > 0 && (
        <div className="mb-3 grid grid-cols-2 gap-1.5 rounded-lg overflow-hidden">
          {post.media.map((m, i) => <img key={i} src={`${backendBase}${m}`} alt="" className="w-full h-40 object-cover" />)}
        </div>
      )}
      {/* Actions */}
      <div className="flex items-center gap-4 pt-2 border-t border-white/5">
        <button onClick={() => onLike(post.id)} className={`flex items-center gap-1 text-xs ${isLiked ? 'text-red-400' : 'text-gray-500 hover:text-red-400'} transition-colors`} data-testid={`like-${post.id}`}>
          <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-red-400' : ''}`} /> {post.like_count || 0}
        </button>
        <button onClick={loadComments} className="flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-400 transition-colors">
          <MessageCircle className="w-3.5 h-3.5" /> {post.comment_count || 0}
        </button>
      </div>
      {/* Comments Section */}
      {showComments && (
        <div className="mt-3 pt-3 border-t border-white/5 space-y-2">
          {comments.map(c => (
            <div key={c.id} className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center shrink-0 overflow-hidden">
                {c.user_avatar ? <img src={`${backendBase}${c.user_avatar}`} alt="" className="w-full h-full object-cover" /> : <User className="w-2.5 h-2.5 text-gray-500" />}
              </div>
              <div>
                <span className="text-[10px] font-semibold">{c.user_name}</span>
                <p className="text-xs text-gray-400">{c.content}</p>
              </div>
            </div>
          ))}
          {token && (
            <div className="flex gap-2 mt-2">
              <Input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder={t ? t('profile.commentPlaceholder') : 'Comment...'} className="h-7 text-xs bg-black/30 border-white/10" onKeyDown={e => e.key === 'Enter' && submitComment()} />
              <Button onClick={submitComment} size="sm" variant="ghost" className="h-7 px-2"><Send className="w-3 h-3" /></Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PublicProfilePage;
